import React, { useEffect, useState } from "react";
import "./AnimatedLoader.css";

/**
 * AnimatedLoader component
 * Displays an accessible animated loading indicator with three pulsing dots
 * Used throughout the app for authentication and AI response loading states
 * 
 * @param {Object} props - Component props
 * @param {string} [props.ariaLabel="Loading"] - Custom aria-label for screen readers
 * @param {string} [props.size="default"] - Size variant: "small", "default", or "large"
 * @param {string} [props.variant="loading"] - Animation variant: "loading" (bounce) or "typing" (left-to-right)
 * @param {string} [props.className] - Additional CSS classes
 */
const AnimatedLoader = ({ 
  ariaLabel = "Loading", 
  size = "default", 
  variant = "loading",
  className = "",
  ...props 
}) => {
  const [activeIndex, setActiveIndex] = useState(1); // Start with middle dot filled

  useEffect(() => {
    // Only use state-based animation for loading variant
    if (variant === "loading") {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % 3); // Cycle through 0,1,2
      }, 500); // Change every 0.5s

      return () => clearInterval(interval);
    }
  }, [variant]);

  const sizeClass = size !== "default" ? ` dot--${size}` : "";
  const variantClass = variant === "typing" ? " loader-container--typing" : "";

  return (
    <div
      className={`loader-container${variantClass} ${className}`}
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      aria-live="polite"
      {...props}
    >
      <span className="sr-only">{ariaLabel}</span>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className={`dot${sizeClass} ${variant === "loading" && activeIndex === index ? "active" : ""}`}
        ></span>
      ))}
    </div>
  );
};

export default AnimatedLoader;